import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import './PaperTemplates.css';

function canEditTemplates(role) {
  return role === 'administrator' || role === 'examiner';
}

function failureText(data, status) {
  if (data && data.error && data.message) return `${data.message} (${data.error})`;
  if (data && data.message) return data.message;
  if (status === 401 || status === 403) return 'Your login was rejected. Log in again, then reopen Paper template.';
  return 'Could not reach the paper templates.';
}

async function templateRequest(path, options = {}) {
  const token = localStorage.getItem('authToken');
  const method = (options.method || 'GET').toUpperCase();
  const send = async () => fetch(`${window.API_BASE_URL || ''}/paper-templates${path}`, {
    method,
    body: options.body,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.body ? { 'Content-Type': 'application/json' } : {})
    }
  });
  let res = await send();
  if (res.status >= 500) res = await send();
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(failureText(data, res.status));
    error.status = res.status;
    throw error;
  }
  return data;
}

export default function PaperTemplates() {
  const { currentUser } = useAuth();
  const canEdit = canEditTemplates(currentUser?.role);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [open, setOpen] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [savedNote, setSavedNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const data = await templateRequest('');
      setTemplates(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setListError(err.message || 'Could not load paper templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openTemplate = (row) => {
    setFormError('');
    setDraftName(row.name || '');
    setDraftBody(row.body || '');
    setOpen({ id: row.id, isNew: false });
  };

  const openNew = () => {
    setFormError('');
    setDraftName('');
    setDraftBody('');
    setOpen({ id: null, isNew: true });
  };

  const close = () => {
    if (saving) return;
    setOpen(null);
    setFormError('');
  };

  const save = async () => {
    const name = draftName.trim();
    if (!name) {
      setFormError('Enter a name for the template.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = JSON.stringify({ name, body: draftBody });
      if (open.isNew) {
        await templateRequest('', { method: 'POST', body: payload });
      } else {
        await templateRequest(`/${open.id}`, { method: 'PUT', body: payload });
      }
      setOpen(null);
      setSavedNote(`Saved “${name}”.`);
      await load();
    } catch (err) {
      setFormError(err.message || 'Could not save the template.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!open || open.isNew) return;
    if (!window.confirm(`Delete "${draftName.trim() || 'this template'}"?`)) return;
    setSaving(true);
    setFormError('');
    try {
      await templateRequest(`/${open.id}`, { method: 'DELETE' });
      setOpen(null);
      await load();
    } catch (err) {
      setFormError(err.message || 'Could not delete the template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rc-upload-card pt-card">
      <label>
        <i className="fas fa-file-lines"></i> Paper template
      </label>
      {savedNote ? <div className="pt-note pt-note--ok">{savedNote}</div> : null}
      {loading ? (
        <div className="pt-note">Loading templates…</div>
      ) : listError ? (
        <div className="pt-note pt-note--error">
          {listError}
          <button type="button" className="pt-retry" onClick={load}>Try again</button>
        </div>
      ) : templates.length === 0 ? (
        <div className="pt-note">No paper templates yet.</div>
      ) : (
        <div className="pt-list">
          {templates.map((row) => (
            <button
              key={row.id}
              type="button"
              className="pt-item"
              onClick={() => openTemplate(row)}
              title={canEdit ? `Edit ${row.name}` : `View ${row.name}`}
            >
              {row.name}
            </button>
          ))}
        </div>
      )}
      {canEdit && (
        <button type="button" className="pt-new" onClick={openNew}>
          <i className="fas fa-plus"></i> New template
        </button>
      )}

      {open && createPortal(
        <div className="pt-overlay" onClick={close}>
          <div
            className="pt-dialog"
            role="dialog"
            aria-label={canEdit ? 'Edit paper template' : 'Paper template'}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pt-dialog__bar">
              <strong>{canEdit ? (open.isNew ? 'New paper template' : 'Edit paper template') : 'Paper template'}</strong>
              {!canEdit && <span className="pt-viewonly">View only</span>}
            </div>
            <label className="pt-field">
              Name
              <input
                className="pt-name"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                readOnly={!canEdit}
                maxLength={200}
                placeholder="Biology: 10th Chapter"
                aria-label="Template name"
              />
            </label>
            <label className="pt-field pt-field--body">
              Text
              <textarea
                className="pt-body"
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value)}
                readOnly={!canEdit}
                spellCheck={false}
                placeholder={'Rubric: Biology: 10th Chapter\n\nTotal marks: 50\n\nPart- 1 Short Questions: (30 marks)\n\nQ.1 Write short answers to the following questions.\n\ni. Define peristalsis. (2 marks)\n\nAns.'}
                aria-label="Template text"
              />
            </label>
            {formError ? <div className="pt-note pt-note--error">{formError}</div> : null}
            <div className="pt-dialog__actions">
              {canEdit && (
                <button type="button" className="pt-btn pt-btn--save" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              )}
              {canEdit && !open.isNew && (
                <button type="button" className="pt-btn pt-btn--delete" onClick={remove} disabled={saving}>
                  Delete
                </button>
              )}
              <button type="button" className="pt-btn" onClick={close} disabled={saving}>
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
